import useTable from '@ui-components-3/ui/lib/components/Table/hooks/useTable'
import makeQuery from 'api/makeQuery'
import { useCallback, useMemo, useState } from 'react'
import User from 'singletons/User'
import CopyIcon from '@ui-components-3/icons/lib/regular/Copy'
import EyeIcon from '@ui-components-3/icons/lib/regular/Eye'
import CommonTable from 'components/common/tables/CommonTable/CommonTable'
import { copyToClipboard } from 'util/clipboard'
import { Navigate } from 'react-router-dom'
import { DASHBOARD } from 'constants/routing'
import moment from 'moment'
import Table from '@ui-components-3/ui/lib/components/Table'
import Placeholder from 'components/Dashboard/common/Placeholder'
import { PaginationState, useFetchServerPaginationTableData } from 'hooks/table/useFetchServerPaginationTableData'
import { PaginationParams } from 'types/api'
import MemberTicketsTable from 'components/tickets/MemberTicketsTable'
import ArrowLeftIcon from '@ui-components-3/icons/lib/solid/ArrowLeft'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import { TicketProvider, useTicketContext } from 'components/tickets/TicketContext'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { isFlaggedOn } from 'components/FeatureFlag'
import MembersFiltersForm from './MembersFiltersForm'
import { ProviderMembersFilterType } from 'types/ProviderMember'
import { format, parse } from 'date-fns'
import { useSelectedMember } from './SelectedMemberProvider'

const initialFilters = { firstName: '', lastName: '', firstNamePreferred: '', dob: '' }

// Inner component that uses the context
const MembersContent = () => {
  const { providerId, isMemberIdFilterFlagEnabled } = User
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('tickets')
  const { selectedTicketId, setSelectedTicketId } = useTicketContext()
  const { setSelectedMemberName } = useSelectedMember()
  const flags = useFlags()
  const isCrisisNotePhase2Enabled = isFlaggedOn('tcCrisisNotePhase2', flags)
  const [filters, setFilters] = useState<ProviderMembersFilterType>(initialFilters)

  type TabType = 'tickets' // Add more tab types as needed

  const handleViewMemberDetails = useCallback(
    (row: any) => {
      const { id, firstName, lastName, firstNamePreferred } = row || {}
      setSelectedMemberId(id)

      // Create a meaningful member name with proper fallbacks
      const displayFirstName = firstNamePreferred || firstName
      const displayLastName = lastName

      // If we have both names, use them; otherwise fall back to member ID
      const memberName = displayFirstName && displayLastName ? `${displayFirstName} ${displayLastName}` : id

      setSelectedMemberName(memberName)
      // Reset ticket selection when changing members
      setSelectedTicketId(null)
    },
    [setSelectedTicketId, setSelectedMemberName],
  )

  const tableColumns = useMemo(() => {
    const columns: any[] = [
      {
        accessorKey: 'firstName',
        header: 'First Name',
        enableHiding: true,
        meta: {
          cellClassName: 'w-[140px]',
        },
      },
      {
        accessorKey: 'firstNamePreferred',
        header: 'Preferred Name',
        enableHiding: true,
        meta: {
          cellClassName: 'w-[140px]',
        },
      },
      {
        accessorKey: 'lastName',
        header: 'Last Name',
        enableHiding: true,
        meta: {
          cellClassName: 'w-[140px]',
        },
      },
      {
        header: 'Member ID',
        accessorKey: 'id',
        enableHiding: true,
        cell: (cell: any) => {
          const { id } = cell?.row?.original || {}
          return (
            <span className="inline-flex gap-2 items-center">
              {id}
              <button
                type="button"
                className="btn-unsized btn-primary-borderless h-10 w-10"
                onClick={(e) => {
                  copyToClipboard(id, 'Member ID has been successfully copied')
                }}
                aria-label="Copy Member ID"
              >
                <CopyIcon aria-hidden="true" />
              </button>
            </span>
          )
        },
        meta: {
          cellClassName: 'w-[200px]',
        },
      },
      {
        accessorKey: 'dob',
        header: 'Date of Birth',
        enableHiding: true,
        cell: (cell: any) => {
          const { dob } = cell?.row?.original || {}
          return dob ? moment(dob, 'YYYY-MM-DD').format('MM-DD-YYYY') : ''
        },
        meta: {
          cellClassName: 'w-[115px]',
        },
      },
    ]
    if (isCrisisNotePhase2Enabled) {
      columns.push({
        id: 'actions',
        header: 'Actions',
        enableHiding: false,
        cell: (cell: any) => {
          const row = cell?.row?.original || {}
          return (
            <button
              type="button"
              className="btn btn-primary-filled rounded-full flex pl-0 py-1.5"
              onClick={() => handleViewMemberDetails(row)}
              aria-label="View Member Details"
              title="View Member Details"
            >
              <EyeIcon aria-hidden="true" className="h-4 w-4 text-primary-700 shrink-0" />
              <span className="text-sm">View Details</span>
            </button>
          )
        },
        meta: {
          cellClassName: 'w-[140px] text-left',
        },
      })
    }
    return columns
  }, [handleViewMemberDetails, isCrisisNotePhase2Enabled])

  const initialPagination: PaginationState = { pageIndex: 0, pageSize: 10 }

  const fetchDataRequest = useCallback(
    async ({ page, filter }: { page: PaginationParams; filter: ProviderMembersFilterType }) => {
      const transformedParams = {
        filter: {
          ...filter,
          dob: filter?.dob ? format(parse(filter.dob, 'MM-dd-yyyy', new Date()), 'yyyy-MM-dd') : undefined,
        },
      }

      return await makeQuery('getProviderMembers', {
        providerId,
        page,
        ...transformedParams,
      })
    },
    [providerId],
  )

  const { data, isLoading, pagination, setPagination } = useFetchServerPaginationTableData({
    fetchDataRequest,
    shouldFetch: !!isMemberIdFilterFlagEnabled,
    initialPagination,
    filters,
  })

  const clearFilters = useCallback(() => {
    setFilters(initialFilters)
  }, [])

  const table = useTable({
    paginationType: 'server',
    data: data.paginated || [],
    columns: tableColumns,
    rowId: 'id',
    isLoading,
    rowCount: data?.meta?.results || 0,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  })

  const hasFilters = useMemo(() => Object.values(filters || {}).filter(Boolean).length, [filters])

  const renderEmptyState = useCallback(
    () => (
      <Table.EmptyBody className="min-h-[450px]">
        <Placeholder
          text={!hasFilters ? 'There is no data to display' : 'No Members Found'}
          description={hasFilters ? 'Try adjusting your filter criteria' : ''}
          icon="queue"
          textClassName="typography-h3"
          iconSize="lg"
          iconColorClassName="text-neutral-500"
        />
      </Table.EmptyBody>
    ),
    [hasFilters],
  )

  const handleBackClick = useCallback(() => {
    setSelectedMemberId(null)
    setSelectedMemberName(null)
    setSelectedTicketId(null)
  }, [setSelectedTicketId, setSelectedMemberName])

  const renderTabContent = () => {
    switch (activeTab) {
      case 'tickets':
        return <MemberTicketsTable memberId={selectedMemberId!} />
      default:
        return null
    }
  }

  if (!isMemberIdFilterFlagEnabled) {
    return <Navigate to={DASHBOARD} replace />
  }

  return (
    <div className="flex-1 overflow-auto min-h-0 pt-5 pb-5 px-2 lg:px-5">
      {selectedMemberId ? (
        <>
          <div className="mb-0 -ml-3 -mt-2">
            <button type="button" className="btn btn-neutral-borderless gap-x-4 self-start" onClick={handleBackClick}>
              <ArrowLeftIcon aria-hidden="true" className="text-secondary-700" />
              Back to Members
            </button>
          </div>

          {/* Tabs navigation */}
          <div className="flex border-b border-neutral-200 mb-6">
            <button
              type="button"
              className={classNames(
                'px-4 py-2 text-lg font-medium focus:outline-none',
                activeTab === 'tickets' ? 'border-b-2 border-black text-black' : 'text-gray-500 hover:text-gray-700',
              )}
              onClick={() => setActiveTab('tickets')}
            >
              Tickets
            </button>
            {/* Add more tabs here as needed */}
          </div>

          {/* Back to All Tickets button - only shown when a ticket is selected */}
          {!!selectedTicketId && (
            <div className="mb-6">
              <button
                type="button"
                className="btn btn-neutral-borderless gap-x-4 self-start"
                onClick={() => setSelectedTicketId(null)}
              >
                <ArrowLeftIcon aria-hidden="true" className="text-secondary-700" />
                Back to All Tickets
              </button>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-[0_8px_24px_0px_rgba(170,170,170,0.25)] px-6 py-8">
            {renderTabContent()}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_8px_24px_0px_rgba(170,170,170,0.25)] px-6 py-8">
          <CommonTable
            model={table}
            aria-label="Members Table"
            prefix={<MembersFiltersForm currentFilters={filters} onApply={setFilters} onClear={clearFilters} />}
            containerClassName="!min-h-[620px] !basis-auto"
            renderEmptyState={renderEmptyState}
          />
        </div>
      )}
    </div>
  )
}

// Wrapper component that provides the context
const Members = () => {
  return (
    <TicketProvider>
      <MembersContent />
    </TicketProvider>
  )
}

export default Members
